import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lunr from 'lunr';
import Search from '../Search';
import { lunrEscapeQuery } from '../../util/search';
import { LIST_ITEM_DELIMITER } from '../../util/form';

class StaticSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: [],
    };

    this.searchIndex = null;
    this.searchData = {};
  }

  componentDidMount() {
    this.createIndex(this.props.suggestions);
  }

  componentDidUpdate(prevProps) {
    const { list, suggestions, value } = this.props;

    // TODO suggestion & list need not be separate (including simple&static comps)
    if (Array.isArray(list)) {
      this.createIndex(list);

      // Did the list change
      if (this.listChanged(prevProps.list, list)) {
        this.createIndex(list);

        if (value) {
          for (const item of list) {
            if (item.itemid && item.itemid === value) {
              this.props.onSuggestionSelected(this.props.getSuggestionValue(item));
              return;
            }
          }
        }
      }
      return;
    }


    this.createIndex(suggestions);
  }

  listChanged = (old, current) => {
    if (old === null && current === null) {
      return false;
    }

    if (old === null && Array.isArray(current)) {
      return true;
    }

    if (old.length !== current.length) {
      return true;
    }

    // Inexpensive but low precision
    if (old.length === current.length) {
      if (!_.isEqual(old[0], current[0])) {
        return true;
      }
    }

    return false;
  };

  onSuggestionSelected = (event, { suggestion }) => {
    if (suggestion === undefined) {
      return this.props.onSuggestionSelected('', '');
    }

    if (suggestion && suggestion.selectAll) {
      for (const s of this.props.suggestions) {
        setTimeout(() => this.props.onSuggestionSelected(this.props.getSuggestionValue(s), s));
      }
      return Promise.resolve();
    }

    return this.props.onSuggestionSelected(this.props.getSuggestionValue(suggestion));
  };

  onSuggestionsClearRequested = () => {
    this.setState({ suggestions: [] });
  };

  getFilteredSuggestions(suggestions, value) {
    const { filterSuggestions, allowSelectAll } = this.props;

    let filteredSuggestions = suggestions;

    if (filterSuggestions) {
      if (typeof filterSuggestions === 'function') {
        filteredSuggestions = filterSuggestions(suggestions);
      }
    }

    if (allowSelectAll && !value && filteredSuggestions && filteredSuggestions.length) {
      filteredSuggestions = [{ name: 'Select All', selectAll: true }].concat(filteredSuggestions);
    }

    return filteredSuggestions || [];
  }

  createIndex(data = []) {
    if (!Array.isArray(data)) {
      return;
    }

    if (!data.length) {
      this.searchIndex = null;
      this.searchData = {};
      return;
    }

    const fields = Object.keys(data[0]);
    const refField = fields[0];

    this.searchIndex = lunr(function () {
      this.pipeline.remove(lunr.stopWordFilter);
      this.pipeline.remove(lunr.stemmer);
      this.searchPipeline.remove(lunr.stopWordFilter);
      this.searchPipeline.remove(lunr.stemmer);

      fields.forEach(field => this.field(field));

      this.ref(refField); // this assumes first field is an id

      data.forEach(entry => this.add(entry));
    });

    this.searchData = _.keyBy(data, refField);
  }

  handleSubmit = value => {
    if (!this.props.highlightFirstSuggestion) {
      this.props.onSuggestionSelected(value, value);
    }
  };

  shouldRenderSuggestions = value => {
    if (!value && this.state.suggestions) {
      return true;
    }

    return value.trim().length > 0;
  };

  loadSuggestions = value => {
    // const { suggestions } = this.props;

    const results = this.searchIndex ? this.searchIndex.search(`${lunrEscapeQuery(value)}*`) : [];

    const suggestions = [];
    results.forEach(result => {
      suggestions.push(this.searchData[result.ref]);
    });

    this.setState({ suggestions: this.getFilteredSuggestions(suggestions, value) });
  };

  renderSuggestion = suggestion => {
    return <div>{suggestion.label || suggestion.name || suggestion.value}</div>;
  };

  render() {
    return (
      <Search
        {...this.props}
        debounce={0}
        highlightFirstSuggestion={false}
        loadSuggestions={this.loadSuggestions}
        onSubmit={this.handleSubmit}
        onSuggestionSelected={this.onSuggestionSelected}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        renderSuggestion={this.renderSuggestion}
        shouldRenderSuggestions={this.shouldRenderSuggestions}
        suggestions={this.state.suggestions}
      />
    );
  }
}

const getSuggestionValue = (suggestion = {}) => {
  if (suggestion && typeof suggestion !== 'object') {
    return suggestion;
  }

  const suggestionKeys = Object.keys(suggestion);
  const itemid = suggestionKeys[0];
  const field = suggestionKeys[1];

  if (!field || !suggestion[field]) {
    return suggestion.itemid;
  }

  return `${suggestion[itemid]}:${LIST_ITEM_DELIMITER}${suggestion[field]}`;
};

StaticSearch.defaultProps = {
  allowSelectAll: false,
  filterSuggestions: v => v,
  getSuggestionValue,
  highlightFirstSuggestion: true,
  list: null,
  onBlur: v => v,
  onChange: v => v,
  selectOnBlur: true,
};

StaticSearch.propTypes = {
  allowSelectAll: PropTypes.bool,
  controlId: PropTypes.string,
  filterSuggestions: PropTypes.func,
  getSuggestionValue: PropTypes.func,
  highlightFirstSuggestion: PropTypes.bool,
  list: PropTypes.array,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  selectOnBlur: PropTypes.bool,
  suggestions: PropTypes.array,
  value: PropTypes.any,
};

export default StaticSearch;
